<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import ConversationAPI from 'dashboard/api/inbox/conversation';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  messageId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
  content: { type: String, default: '' },
  attachments: { type: Array, default: () => [] },
  isPrivate: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const selectedConversationId = ref(null);
const isForwarding = ref(false);

const allConversations = useMapGetter('getAllConversations');
const getInbox = useMapGetter('inboxes/getInbox');

// Conversations already loaded in the store, used as the default (empty query) list.
const recentConversations = computed(() =>
  allConversations.value.filter(
    conversation => conversation.id !== Number(props.conversationId)
  )
);

const conversationsToShow = computed(() =>
  searchQuery.value.trim() ? searchResults.value : recentConversations.value
);

const isFormInvalid = computed(() => !selectedConversationId.value);

const contactNameFor = conversation =>
  conversation.meta?.sender?.name || t('CONVERSATION.FORWARD_MODAL.UNKNOWN_CONTACT');

const inboxNameFor = conversation => getInbox.value(conversation.inbox_id)?.name || '';

// Attachments don't carry a filename, only a file type and extension.
const attachmentLabelFor = attachment =>
  attachment.extension
    ? `${attachment.fileType}.${attachment.extension}`
    : attachment.fileType;

const runSearch = useDebounceFn(async query => {
  if (!query) {
    searchResults.value = [];
    return;
  }

  isSearching.value = true;
  try {
    const {
      data: { data },
    } = await ConversationAPI.search({ q: query });
    searchResults.value = (data.payload || []).filter(
      conversation => conversation.id !== Number(props.conversationId)
    );
  } catch (error) {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
}, 300);

watch(searchQuery, value => runSearch(value.trim()));

const selectConversation = conversation => {
  selectedConversationId.value = conversation.id;
};

const resetState = () => {
  searchQuery.value = '';
  searchResults.value = [];
  selectedConversationId.value = null;
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (isFormInvalid.value) return;

  isForwarding.value = true;
  try {
    await store.dispatch('forwardMessage', {
      conversationId: props.conversationId,
      messageIds: [props.messageId],
      targetConversationIds: [selectedConversationId.value],
    });
    useAlert(t('CONVERSATION.FORWARD_MODAL.SUCCESS'));
    close();
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('CONVERSATION.FORWARD_MODAL.ERROR'));
  } finally {
    isForwarding.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONVERSATION.FORWARD_MODAL.TITLE')"
    :confirm-button-label="t('CONVERSATION.FORWARD_MODAL.SUBMIT')"
    :is-loading="isForwarding"
    :disable-confirm-button="isFormInvalid"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-black2">
        <span
          v-if="isPrivate"
          class="self-start px-2 py-0.5 text-xs font-medium rounded-full text-n-amber-11 bg-n-amber-3"
        >
          {{ t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
        </span>
        <p v-if="content" class="mb-0 text-sm line-clamp-3 text-n-slate-12">
          {{ content }}
        </p>
        <div v-if="attachments.length" class="flex flex-wrap gap-2">
          <div
            v-for="attachment in attachments"
            :key="attachment.id"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            <Icon icon="i-lucide-paperclip" class="flex-shrink-0 size-3" />
            <span class="truncate max-w-[10rem]">
              {{ attachmentLabelFor(attachment) }}
            </span>
          </div>
        </div>
      </div>

      <Input
        v-model="searchQuery"
        :placeholder="t('CONVERSATION.FORWARD_MODAL.SEARCH_PLACEHOLDER')"
        autofocus
      />

      <ul class="flex flex-col gap-1 pl-0 m-0 overflow-y-auto list-none max-h-64">
        <li
          v-if="!isSearching && !conversationsToShow.length"
          class="py-4 text-sm text-center text-n-slate-11"
        >
          {{ t('CONVERSATION.FORWARD_MODAL.NO_CONVERSATIONS') }}
        </li>
        <li
          v-for="conversation in conversationsToShow"
          :key="conversation.id"
          role="button"
          class="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
          :class="{
            'bg-n-alpha-2': selectedConversationId === conversation.id,
          }"
          @click="selectConversation(conversation)"
        >
          <Avatar
            :name="contactNameFor(conversation)"
            :src="conversation.meta?.sender?.thumbnail"
            :size="28"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-sm truncate text-n-slate-12">
              {{ contactNameFor(conversation) }}
            </span>
            <span
              v-if="inboxNameFor(conversation)"
              class="text-xs truncate text-n-slate-11"
            >
              {{ inboxNameFor(conversation) }}
            </span>
          </div>
          <Icon
            v-if="selectedConversationId === conversation.id"
            icon="i-lucide-check"
            class="flex-shrink-0 ml-auto text-n-brand size-4"
          />
        </li>
      </ul>
    </div>
  </Dialog>
</template>
